import { Messages, MessageType } from "../constants/Messages";


// for material type/size creation
export const formatErrorMessage = (errors: { message: string, value?: string }[]): string => {
    if (!errors || errors.length === 0) {
        return "";
    }
    const errorList = errors
        .map((error, idx) => {
            const recordIndex = `Record #${idx + 1}`;
            return `${idx + 1}. ${recordIndex}: ${error.message} ${error.value ? `- ${error.value}` : ""}`;
        })
        .join("\n");
    if (!errorList) {
        return "⚠️ No valid error messages to display.";
    }

    return `⚠️ One or more records were not processed successfully due to:\n\n${errorList}`;
};

// Now handleResponseAlerts accepts formatSuccessMessage as a parameter
export const handleMaterialResponseAlerts = (
    response: any,
    setSuccessAlert: React.Dispatch<React.SetStateAction<{ message: string; type?: MessageType } | null>>,
    setErrorAlert: React.Dispatch<React.SetStateAction<{ message: string; type?: MessageType } | null>>,
    formatSuccessMessage: (results: any[]) => string, // Accept formatSuccessMessage function as a parameter
) => {
    const successMessage = formatSuccessMessage(response.results); // Use the provided formatSuccessMessage
    const errorMessage = formatErrorMessage(response.errors);
    if (response.results && response.results.length > 0) {
        setSuccessAlert({
            message: successMessage,
        });
    }
    if (response.errors && response.errors.length > 0) {
        setErrorAlert({
            message: errorMessage,
        });
    }
};


// Format success message for inventory
export const formatSuccessMessageForInventory = (results: any[]): string => {
    if (!results || results.length === 0) {
        return "";
    }
    return results
        .map(
            (record, idx) =>
                `✅ Record ${idx + 1}:\n` +
                `   - Material: ${record.material_type}\n` +
                `   - Size: ${record.material_size}\n` +
                `   - Color: ${record.color_name} (${record.color_code})\n` +
                `   - Quantity: ${record.quantity} ${record.quantity_unit}\n`,
        )
        .join("\n");
};

export const formatSuccessMessageForUpdateInventory = (results: any[]): string => {
    if (!results || results.length === 0) {
        return "";
    }
    return results
        .map(
            (record, idx) =>
                `✅ Record ${idx + 1}:\n` +
                `   - Material: ${record.material_type}\n` +
                `   - Size: ${record.material_size}\n` +
                `   - Color: ${record.color_name} (${record.color_code})\n`,
        )
        .join("\n");
};

// Common handler for response alerts
export const handleInventoryResponseAlerts = (
    response: any,
    setSuccessAlert: any,
    setErrorAlert: any,
) => {
    if (response.results.length > 0) {
        const message = formatSuccessMessageForInventory(response.results);
        setSuccessAlert({
            message,
        });
    } else {
        setSuccessAlert(null);
    }
    if (response.errors.length > 0) {
        const errorMessage = formatErrorMessage(response.errors);
        setErrorAlert({
            message: errorMessage,
        });
    } else {
        setErrorAlert(null);
    }
};

export const catchError = (error: any, setErrorAlert: any) => {
    const errorMessage = error.message || "An unknown error occurred.";
    const detailedErrors = error.errors || [];

    // Display detailed errors if present
    if (detailedErrors.length > 0) {
        console.error("Detailed errors:", detailedErrors);
    }

    // Set error alert with the message
    setErrorAlert({
        title: errorMessage,
        message: `${formatErrorMessage(detailedErrors)}`,
    });
};

// Common handler for response alerts
export const handleInventoryUpdateResponseAlerts = (
    response: any,
    setSuccessAlert: any,
    setErrorAlert: any,
) => {
    if (response.results.length > 0) {
        const message = formatSuccessMessageForUpdateInventory(response.results);
        setSuccessAlert({
            message,
        });
    } else {
        setSuccessAlert(null);
    }
    if (response.errors.length > 0) {
        const errorMessage = formatErrorMessage(response.errors);
        setErrorAlert({
            message: errorMessage,
        });
    } else {
        setErrorAlert(null);
    }
};

// Check for duplicate entries in the inventory
export const checkForDuplicates = (items: any[]): boolean => {
    const seen = new Set();
    for (const item of items) {
        const key = formatUniqueKeyCombination(item);
        if (seen.has(key)) {
            console.error("duplicated", key);
            return true; // Duplicate found
        }
        seen.add(key);
    }
    return false; // No duplicates
};

export const normalizeString = (value?: string): string => {
    return value ? value.trim().replace(/\s+/g, "").toLowerCase() : "";
};

export const formatUniqueKeyCombination = (item: any): string => {
    const normalizedColorCode = normalizeString(
        item.color_code?.trim() ||
        `${item.material_type_id}-${(item.color_name?.trim() || '')}`,
    ); // Normalize color_code or fallback to material_type_id and color_name

    return `${item.material_type_id}-${item.material_size_id}-${normalizedColorCode}`;
};


export const generateErrorDetailsHtml = (errors: string): string => {
    return `
        <div class="error-summary border rounded p-4">
            <button
                class="toggle-details-button text-blue-600 hover:text-blue-800 font-semibold mb-4"
                onclick="
                    const details = this.nextElementSibling;
                    const isHidden = details.classList.contains('hidden');
                    details.classList.toggle('hidden', !isHidden);
                    this.innerText = isHidden ? '▼ Hide Details' : '▶ Show Details';
                "
            >
                ▶ Show Details
            </button>
            <ul class="list-disc pl-5 hidden">
                ${errors}
            </ul>
        </div>
    `;
};

export const generateDetailedErrorHtml = (parsedRows: Record<string, any>[]): string => {
    return parsedRows
        .map((row, idx) => {
            const recordDetails = Object.entries(row)
                .map(
                    ([ key, value ]) =>
                        `<div class="flex items-center gap-2 mb-1">
                            <span class="font-semibold text-gray-700">${key}:</span>
                            <span class="text-gray-600">${value || "-"}</span>
                        </div>`,
                )
                .join("");
            return `
                <li class="mb-4">
                    <div class="font-bold text-md text-blue-600">Record #${idx + 1}</div>
                    <div class="p-2 rounded">
                        ${recordDetails}
                    </div>
                </li>
            `;
        })
        .join("");
};

export const processInvalidRows = (invalidRowsList: string[]) => {
    const parsedRows = invalidRowsList
        .map((row) => {
            try {
                return JSON.parse(row);
            } catch (error) {
                console.error("Error parsing row:", row);
                return null;
            }
        })
        .filter(Boolean);

    return generateDetailedErrorHtml(parsedRows);
};

export const renderErrorDetails = (invalidRowsList: any, setErrorAlert: any) => {
    const errorDetails = generateErrorDetailsHtml(processInvalidRows(invalidRowsList));

    if (invalidRowsList.length > 0) {
        setErrorAlert({
            title: `⚠️ There are ${invalidRowsList.length} invalid rows. Please check your data.`,
            message: errorDetails,
            isHtml: true,
        });
    }
};


export const processInvalidRowsForUpdate = (errors: any, setItems: any) => {
    if (errors.length > 0 && Array.isArray(errors)) {
        const invalidItems = errors.map((error: any) => ({
            material_type_id: error.record?.material_type_id || 0,
            material_size_id: error.record?.material_size_id || 0,
            color_code: error.record?.color_code || "",
            is_new: error.record?.is_new || false,
            quantity_changed: error.record?.quantity_changed || 0,
            remaining_quantity: error.record?.remaining_quantity || 0,
            quantity_unit: error.record?.quantity_unit || null,
        }));
        setItems(invalidItems);
    }
};

export const renderInvalidFormErrors = (items: any, isFormValid: any, setErrorAlert: any) => {
    if (!isFormValid(items)) {
        setErrorAlert({
            message: Messages.ERROR_INVALID_FORM,
        });
        return;
    }

    if (checkForDuplicates(items)) {
        setErrorAlert({
            message: Messages.ERROR_DUPLICATE_ENTRY,
        });
        return;
    }
};

export const noDataValidation = (data: any, setErrorAlert: any) => {
    if (!data.length) {
        setErrorAlert({
            message: Messages.ERROR_NO_VALID_DATA,
        });
        return;
    }
};